import * as React from 'react';
import { LayoutChangeEvent, Platform, StyleSheet, View } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  doReportingClosePage,
  doReportingSelectReportOpen,
  doSetSidebar,
  NAVIGATE_MODAL,
  REMOTE_ACCOUNT_LOAD_TFA,
} from 'store/actions';
import { useSelector } from 'hooks/useSelector';
import {
  selectRptReportPages,
  selectRptReportPagesSaved,
} from 'store/remote/selectors';
import { ICONS } from 'components/Icon';
import { Page } from 'components/Page';
import { Card } from 'components/Card';
import { CardRow, CardRowProps } from 'components/CardRow';
import { theme } from 'lib/theme';
import { Logo } from 'components/Logo';
import { IconButton } from 'components/IconButton';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { MenuProps } from 'components/Menu';
import { ScrollView } from 'components/ScrollView';
import { UserAvatar } from 'components/UserAvatar';
import { ItemDetails } from 'components/ItemDetails';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import {
  selectUserEmail,
  selectUserName,
  selectUserTfaEnabled,
  selectUserTfaRequired,
} from 'store/auth/selectors';
import { ModalScreens, Screens } from 'components/Navigation/types';
import { APP_VERSION } from 'lib/getDeviceInformation';
import { NAVIGATION_HEADER_HEIGHT, PageHeader } from 'components/PageHeader';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { DrawerActions } from '@react-navigation/native';
import { GapMedium } from 'components/GapMedium';
import { navigationRef } from 'components/Navigation/navigationRef';
import {
  selectAppType,
  selectAppUpdateIsAvailable,
  selectSidebar,
} from 'store/app/selectors';
import { IsInModalProvider } from 'hooks/isInModal';

export const PageNavigation: React.FC = () => {
  const dispatch = useDispatch();

  const appType = useSelector(selectAppType);
  const isSidebar = appType !== 'mobile';

  const reportPagesSaved = useSelector(selectRptReportPagesSaved);
  const reportPages = useSelector(selectRptReportPages);
  const hasPages = !!reportPagesSaved.length || !!reportPages.length;

  const isTfaRequired = useSelector(selectUserTfaRequired);
  const isTfaEnabled = useSelector(selectUserTfaEnabled);
  const appUpdateIsAvailable = useSelector(selectAppUpdateIsAvailable);

  const [currentRouteName, setCurrentRouteName] = React.useState<string | null>(
    null,
  );
  const [navigationListHeight, setNavigationListHeight] = React.useState(0);
  const [openPageRowHeight, setOpenPageRowHeight] = React.useState(0);
  const [navigationViewportHeight, setNavigationViewportHeight] =
    React.useState(0);

  React.useEffect(() => {
    const unsubscribe = navigationRef.current?.addListener('state', (event) => {
      const rootState = event.data.state?.routes.find(
        (r) => r.name === Screens.RootPage,
      )?.state;
      const state = rootState?.routes.find(
        (r) => r.name === Screens.TAB_HOME,
      )?.state;
      if (state && state.routes && state.index !== undefined) {
        setCurrentRouteName(state.routes[state.index]?.name || null);
      }
    });
    return () => {
      unsubscribe?.();
    };
  }, []);

  const handleNavigate = (pageKey: string) => {
    navigationRef.current?.navigate(Screens.RootPage, {
      screen: Screens.TAB_HOME,
      params: {
        screen: pageKey,
      },
    });
  };

  const handleGetHelp = () => {
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.Support,
        params: {},
      }),
    );
  };

  const handleOpenNewPage = React.useCallback(() => {
    dispatch(doReportingSelectReportOpen());
  }, [dispatch]);

  const userName = useSelector(selectUserName);
  const userEmail = useSelector(selectUserEmail);
  const sidebar = useSelector(selectSidebar);

  const [logoWidth, setLogoWidth] = React.useState(0);
  const handleLogoLayout = (event: LayoutChangeEvent) => {
    if (event.nativeEvent.layout.width) {
      setLogoWidth(Math.min(event.nativeEvent.layout.width, 100));
    }
  };

  const { top: safeAreaTop } = useSafeAreaInsets();

  const marginTop =
    Platform.OS === 'ios' ? safeAreaTop || 0 : safeAreaTop || theme.padding / 2;

  const getCardRowProps = React.useCallback(
    (isCurrent: boolean): Partial<CardRowProps> =>
      isSidebar
        ? {
            selected: isCurrent,
            labelWeight: 'regular',
            labelSize: 'xsmall',
            style: {
              backgroundColor: isCurrent ? theme.colors.blue10 : 'transparent',
              minHeight: 0,
            },
            hoveredStyle: {
              backgroundColor: theme.colors.blue10,
            },
            small: true,
            iconPressableStyle: isSidebar
              ? { paddingLeft: 0, paddingRight: 0 }
              : undefined,
          }
        : {
            selected: isCurrent,
            iconPressableStyle: isSidebar
              ? { paddingLeft: 0, paddingRight: 0 }
              : undefined,
          },
    [isSidebar],
  );

  const isNavigationOverflowing =
    Platform.OS !== 'ios' &&
    Platform.OS !== 'android' &&
    navigationListHeight + openPageRowHeight > navigationViewportHeight + 1;

  const openPageRow = React.useMemo(
    () => (
      <Card withShadow={false}>
        <CardRow
          onPress={handleOpenNewPage}
          style={{
            backgroundColor: 'transparent',
            minHeight: 0,
          }}
          hoveredStyle={{
            backgroundColor: theme.colors.blue10,
          }}
          small={isSidebar}
          label="Open New Page"
          icon={ICONS.PlusLight}
          iconColor={theme.colors.blue}
          labelColor={theme.colors.blue}
          iconPressableStyle={
            isSidebar ? { paddingLeft: 0, paddingRight: 0 } : undefined
          }
        />
      </Card>
    ),
    [handleOpenNewPage, isSidebar],
  );

  const Navigation = React.useMemo(() => {
    return (
      <GapMedium style={{ paddingBottom: theme.padding }}>
        <View
          onLayout={(event) =>
            setNavigationListHeight(event.nativeEvent.layout.height)
          }
        >
          {!!reportPagesSaved.length && (
            <ViewWithSeparators
              separatorColor="transparent"
              separatorMargin={!isSidebar ? theme.padding / 4 : 0}
            >
              {reportPagesSaved.map((page) => {
                const isCurrent = currentRouteName === page.key;
                const menuItems: MenuProps['options'] = [
                  {
                    text: 'Open Page',
                    onPress: !isCurrent
                      ? () => handleNavigate(page.key)
                      : undefined,
                  },
                ];
                if (page.closeable) {
                  menuItems.push({
                    text: 'Close Page',
                    onPress: () =>
                      dispatch(doReportingClosePage({ key: page.key })),
                    destructive: true,
                  });
                }
                return (
                  <Card key={page.key || page.id} withShadow={false}>
                    <CardRow
                      icon={page.icon ?? ICONS.FileLight}
                      label={page.title}
                      value={page.menuDescription}
                      labelAlignTop
                      valueNumberOfLines={1}
                      onPress={() => handleNavigate(page.key)}
                      menuOptions={menuItems}
                      hideMenuButton
                      {...getCardRowProps(isCurrent)}
                    />
                  </Card>
                );
              })}
            </ViewWithSeparators>
          )}
          {!!reportPagesSaved.length && !!reportPages.length && (
            <SeparatorHorizontal
              marginHorizontal={theme.padding}
              marginVertical={theme.padding}
            />
          )}
          {!!reportPages.length && (
            <ViewWithSeparators
              separatorColor="transparent"
              separatorMargin={!isSidebar ? theme.padding / 4 : 0}
            >
              {reportPages.map((page) => {
                const isCurrent = currentRouteName === page.key;
                const menuItems: MenuProps['options'] = [
                  {
                    text: 'Open Page',
                    onPress: !isCurrent
                      ? () => handleNavigate(page.key)
                      : undefined,
                  },
                ];
                if (page.closeable) {
                  menuItems.push({
                    text: 'Close Page',
                    onPress: () =>
                      dispatch(doReportingClosePage({ key: page.key })),
                    destructive: true,
                  });
                }
                return (
                  <Card key={page.key || page.id} withShadow={false}>
                    <CardRow
                      icon={page.icon ?? ICONS.FileLight}
                      label={page.title}
                      value={page.menuDescription}
                      valueNumberOfLines={1}
                      labelAlignTop
                      onPress={() => handleNavigate(page.key)}
                      menuOptions={menuItems}
                      hideMenuButton={isSidebar}
                      {...getCardRowProps(isCurrent)}
                    />
                  </Card>
                );
              })}
            </ViewWithSeparators>
          )}
        </View>
        {!isNavigationOverflowing && (
          <View
            onLayout={(event) =>
              setOpenPageRowHeight(event.nativeEvent.layout.height)
            }
          >
            <SeparatorHorizontal marginHorizontal={theme.padding} />
            <View style={{ paddingTop: theme.padding }}>{openPageRow}</View>
          </View>
        )}
      </GapMedium>
    );
  }, [
    getCardRowProps,
    currentRouteName,
    dispatch,
    isSidebar,
    isNavigationOverflowing,
    openPageRow,
    reportPages,
    reportPagesSaved,
  ]);

  return (
    <IsInModalProvider value={isSidebar ? 'windowed' : false}>
      <Page
        scrollView={!isSidebar}
        backgroundColor={isSidebar ? 'transparent' : undefined}
        headerBackgroundColor={isSidebar ? 'transparent' : undefined}
        withSafeAreaBottom={!isSidebar}
        keyboardAvoiding={false}
        header={
          <PageHeader
            gap={0}
            ignoreSafeArea={isSidebar}
            style={
              isSidebar
                ? // Sidebar has no safe area - ensure the header aligns with other headers by removing the margin top
                  {
                    minHeight: NAVIGATION_HEADER_HEIGHT - marginTop,
                    paddingTop: 16,
                  }
                : undefined
            }
            // Get help button (mobile only)
            onPressCloseButton={!isSidebar ? handleGetHelp : undefined}
            closeIcon={ICONS.CommentQuestionLight}
          >
            {!isSidebar ? (
              <View
                style={{
                  flex: 1,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onLayout={handleLogoLayout}
              >
                <Logo width={logoWidth} />
              </View>
            ) : (
              <View
                style={{
                  flexDirection: 'row',
                  flex: undefined,
                  alignItems: 'center',
                }}
              >
                <View style={{ flex: 1 }} onLayout={handleLogoLayout}>
                  <Logo width={logoWidth} />
                </View>
                {sidebar.canBePinned && (
                  <IconButton
                    icon={
                      sidebar.isPinned
                        ? ICONS.ThumbtackAngleSlashDuotoneSolid
                        : ICONS.ThumbtackAngleDuotoneSolid
                    }
                    onPress={() => {
                      dispatch(
                        doSetSidebar({
                          isPinned: !sidebar.isPinned,
                        }),
                      );
                      navigationRef.current?.dispatch(
                        DrawerActions.closeDrawer(),
                      );
                    }}
                    disabled={!hasPages}
                  />
                )}
              </View>
            )}
          </PageHeader>
        }
      >
        {isSidebar ? (
          <View style={{ flex: 1 }}>
            <ScrollView
              backgroundColor="transparent"
              contentPaddingTop={0}
              withSafeAreaTop
              withSafeAreaBottom={false}
              withSafeAreaLeft={false}
              withSafeAreaRight={false}
              onLayout={(event) =>
                setNavigationViewportHeight(event.nativeEvent.layout.height)
              }
            >
              {Navigation}
            </ScrollView>
            {isNavigationOverflowing && (
              <>
                <SeparatorHorizontal />
                <View
                  onLayout={(event) =>
                    setOpenPageRowHeight(event.nativeEvent.layout.height)
                  }
                >
                  <View
                    style={{
                      paddingTop: theme.padding,
                      paddingBottom: theme.padding,
                    }}
                  >
                    {openPageRow}
                  </View>
                </View>
              </>
            )}
            <SeparatorHorizontal />
            <View
              style={{
                paddingVertical: theme.padding,
                paddingHorizontal: theme.padding,
                gap: theme.paddingHorizontalHalf / 2,
                flexDirection: 'column',
              }}
            >
              <Card marginHorizontal={false} withShadow={false}>
                <CardRow
                  onPress={() => handleNavigate('UserProfile')}
                  style={{
                    backgroundColor:
                      currentRouteName === 'UserProfile'
                        ? theme.colors.blue10
                        : 'transparent',
                  }}
                  childrenStyle={{
                    alignItems: 'center',
                    flexDirection: 'row',
                    justifyContent: 'center',
                  }}
                  hoveredStyle={{
                    backgroundColor: theme.colors.blue10,
                  }}
                  compact
                  // disabled
                >
                  <UserAvatar
                    size={31}
                    style={{ marginRight: theme.padding }}
                    previewOnPress={false}
                  />
                  <ItemDetails
                    title={userName || ''}
                    subtitle={userEmail || ''}
                    titleColor={theme.colors.textBlack64}
                    subtitleColor={theme.colors.textBlack64}
                    small
                  />
                </CardRow>
              </Card>
              {isTfaRequired && !isTfaEnabled && (
                <Card marginHorizontal={false} withShadow={false}>
                  <CardRow
                    onPress={() => dispatch(REMOTE_ACCOUNT_LOAD_TFA())}
                    style={{
                      backgroundColor: 'transparent',
                    }}
                    hoveredStyle={{
                      backgroundColor: theme.colors.blue10,
                    }}
                    compact
                    label="MFA Required"
                    value="Enable MFA now"
                    labelAlignTop
                    icon={ICONS.InputNumberDuo}
                    iconColor={theme.colors.white}
                    iconPressableStyle={styles.footerCardRowIconPressable}
                    labelColor={theme.colors.textBlack64}
                    labelWeight="bold"
                  />
                </Card>
              )}
              <Card marginHorizontal={false} withShadow={false}>
                <CardRow
                  onPress={() => handleNavigate('AppSettings')}
                  style={{
                    backgroundColor:
                      currentRouteName === 'AppSettings'
                        ? theme.colors.blue10
                        : 'transparent',
                  }}
                  hoveredStyle={{
                    backgroundColor: theme.colors.blue10,
                  }}
                  label="Settings"
                  labelAlignTop
                  icon={ICONS.GearSolid}
                  iconColor={theme.colors.white}
                  iconPressableStyle={styles.footerCardRowIconPressable}
                  labelColor={theme.colors.textBlack64}
                  labelWeight="bold"
                  actionIcon={ICONS.AsteriskSolid}
                  onAction={
                    appUpdateIsAvailable
                      ? () =>
                          dispatch(
                            NAVIGATE_MODAL({
                              screen: ModalScreens.UpgradeRequired,
                              params: {
                                major: appUpdateIsAvailable === 'major',
                                withClose: true,
                              },
                            }),
                          )
                      : undefined
                  }
                />
              </Card>
            </View>
            <SeparatorHorizontal />
            <View
              style={{
                paddingVertical: theme.padding,
              }}
            >
              <Card withShadow={false}>
                <CardRow
                  onPress={handleGetHelp}
                  style={{
                    backgroundColor: 'transparent',
                    minHeight: 0,
                  }}
                  small
                  label={`Get Help`}
                  icon={ICONS.CommentQuestionLight}
                  value={`v${APP_VERSION}`}
                  labelColor={theme.colors.blue}
                  iconColor={theme.colors.blue}
                  hoveredStyle={{
                    backgroundColor: theme.colors.blue10,
                  }}
                ></CardRow>
              </Card>
            </View>
          </View>
        ) : (
          Navigation
        )}
      </Page>
    </IsInModalProvider>
  );
};

const styles = StyleSheet.create({
  footerCardRowIconPressable: {
    backgroundColor: '#999999',
    borderRadius: 99,
    width: 31,
    aspectRatio: 1,
    marginLeft: 0,
    marginRight: theme.padding,
  },
});
